import {
  Customer,
  CustomerSector,
  Package,
  AddressData,
  SubscriptionStatusGroup,
  SubscriptionStatus,
  SubscriptionType,
  Subscription,
} from '../model/customer.model'

export function getFullName(customer?: Customer) {
  const personalData = customer?.personal
  const fullName = [personalData?.firstName, personalData?.middleName, personalData?.lastName]
    .filter((val) => Boolean(val))
    .join(' ')

  return fullName
}

export const getPackages = (customer: Customer) =>
  customer?.accounts?.reduce(
    (acum, account) => acum.concat([...account?.packages]),
    [] as Package[],
  )

export const getPackageFromProductId = (customer?: Customer, productId?: string) => {
  if (customer && productId) {
    return getPackages(customer).find(
      (pkg: Package) =>
        pkg.subscriptions[pkg.subscriptions.findIndex((i) => i.identificationNr === productId)],
    )
  }
  return undefined
}

export const getPackagesByAccountNumber = (customer: Customer, accountId?: string) => {
  const currentAccount = customer?.accounts?.filter(
    (account) => account.accountNumber === accountId,
  )

  return currentAccount[0]?.packages || []
}

export const getSubscriptionsFromProductId = (customer: Customer, productId: string) => {
  const pkg = getPackageFromProductId(customer, productId)
  return pkg && pkg.subscriptions.filter((sub) => sub.identificationNr === productId)
}

export const getNonFixedSubscriptionFromProductId = (customer: Customer, productId: string) => {
  const subscriptions = getSubscriptionsFromProductId(customer, productId)
  return subscriptions && subscriptions.find((sub) => sub.type !== 'DIGITAL_FIXED')
}

export const getFixedSubscriptionFromProductId = (customer: Customer, productId: string) => {
  const subscriptions = getSubscriptionsFromProductId(customer, productId)
  return subscriptions && subscriptions.find((sub) => sub.type === 'DIGITAL_FIXED')
}

export const getAccountFromProductId = (customer: Customer, productId: string) =>
  customer.accounts.find((account) =>
    account.packages.find((packageData) =>
      packageData.subscriptions.find((sub) => sub.identificationNr === productId),
    ),
  )

export const getAccountFromSubscriptionId = (customer: Customer, subscriptionId: string) =>
  customer.accounts.find((account) =>
    account.packages.find((packageData) =>
      packageData.subscriptions.find((sub) => sub.id === subscriptionId),
    ),
  )

export const getTariffDescription = (packageData: Package) =>
  packageData.bundle?.typeDescription ||
  packageData.subscriptions[0]?.commercialProduct.description ||
  ''

export function getSegmentText(
  segment: CustomerSector,
  translate: (id: string, defaultMessage: string, values?: { [key: string]: string }) => string,
) {
  const segmentTxt = {
    [CustomerSector.RESIDENTIAL]: translate('residential', 'Residencial'),
    [CustomerSector.COMPANY]: translate('company', 'Negocio'),
    [CustomerSector.HORECA]: translate('horeca', 'Horeca'),
    [CustomerSector.SELF_EMPLOYED]: translate('self_employed', 'Autónomo'),
    [CustomerSector.UNKNOWN]: translate('unknown', 'Segmento desconocido'),
  }

  return segmentTxt[segment]
}

export function formatAddress(address: AddressData) {
  const mainAddress = address.address || ''
  const zipCode = address.zipCode || ''
  const locality = address.locality || ''
  const state = address.state || ''
  const addressSeparator = mainAddress && '.'

  const secondaryInfo = `${zipCode} - ${locality} (${state})` || ''

  return `${mainAddress}${addressSeparator} ${secondaryInfo}`
}

export const subscriptionStatusGroupText = {
  [SubscriptionStatusGroup.ACTIVE]: {
    id: SubscriptionStatusGroup.ACTIVE,
    text: 'Activo',
    letter: 'A',
  },
  [SubscriptionStatusGroup.LOCKED]: {
    id: SubscriptionStatusGroup.LOCKED,
    text: 'Bloqueado',
    letter: 'B',
  },
  [SubscriptionStatusGroup.NO_GROUP]: {
    id: SubscriptionStatusGroup.NO_GROUP,
    text: 'No status group',
    letter: 'N',
  },
  [SubscriptionStatusGroup.ON_FLY]: {
    id: SubscriptionStatusGroup.ON_FLY,
    text: 'En vuelo',
    letter: 'V',
  },
  [SubscriptionStatusGroup.PENDING]: {
    id: SubscriptionStatusGroup.PENDING,
    text: 'Pendiente',
    letter: 'P',
  },
  [SubscriptionStatusGroup.TERMINATED]: {
    id: SubscriptionStatusGroup.TERMINATED,
    text: 'Terminado',
    letter: 'T',
  },
}

export const subscriptionStatusText = {
  [SubscriptionStatus.INSTALLED]: {
    id: SubscriptionStatus.INSTALLED,
    text: 'Instalado',
  },
  [SubscriptionStatus.PREACTIVATED]: {
    id: SubscriptionStatus.PREACTIVATED,
    text: 'Pre-activado',
  },
  [SubscriptionStatus.ACTIVATED]: {
    id: SubscriptionStatus.ACTIVATED,
    text: 'Activo',
  },
  [SubscriptionStatus.LOCKED_DUE_BALANCE]: {
    id: SubscriptionStatus.LOCKED_DUE_BALANCE,
    text: 'Bloqueo por saldo',
  },
  [SubscriptionStatus.TERMINATED]: {
    id: SubscriptionStatus.TERMINATED,
    text: 'Terminado',
  },
  [SubscriptionStatus.TEMPORARY_LOCKED]: {
    id: SubscriptionStatus.TEMPORARY_LOCKED,
    text: 'Bloqueo temporal',
  },
  [SubscriptionStatus.TERMINATION_PENDING]: {
    id: SubscriptionStatus.TERMINATION_PENDING,
    text: 'Pendiente terminación',
  },
  [SubscriptionStatus.LOCKED_DUE_CONSUMPTION_LIMIT]: {
    id: SubscriptionStatus.LOCKED_DUE_CONSUMPTION_LIMIT,
    text: 'Bloqueo límite consumo',
  },
  [SubscriptionStatus.SUSPENSION_PENDING]: {
    id: SubscriptionStatus.SUSPENSION_PENDING,
    text: 'Suspensión pendiente',
  },
  [SubscriptionStatus.RECONNECTION_PENDING]: {
    id: SubscriptionStatus.RECONNECTION_PENDING,
    text: 'Reconexión pendiente',
  },
  [SubscriptionStatus.OK]: {
    id: SubscriptionStatus.OK,
    text: 'OK',
  },
  [SubscriptionStatus.TEMPORARY_LOCK_PENDING]: {
    id: SubscriptionStatus.TEMPORARY_LOCK_PENDING,
    text: 'Pendiente bloqueo temporal',
  },
  [SubscriptionStatus.UNKNOWN]: {
    id: SubscriptionStatus.UNKNOWN,
    text: 'Desconocido',
  },
}

export const getSubscriptionFromId = (customer: Customer, subscriptionId: string) => {
  const pkgWithSubscription = getPackages(customer).find((pkg) =>
    pkg.subscriptions.find((sub) => `${sub.id}` === subscriptionId),
  )

  return (
    pkgWithSubscription &&
    pkgWithSubscription.subscriptions.find((sub) => `${sub.id}` === subscriptionId)
  )
}

export const subscriptionTypeText = {
  [SubscriptionType.PREPAID]: {
    id: SubscriptionType.PREPAID,
    text: 'Prepago',
  },
  [SubscriptionType.POSTPAID]: {
    id: SubscriptionType.POSTPAID,
    text: 'Postpago',
  },
  [SubscriptionType.ANALOG_FIXED]: {
    id: SubscriptionType.ANALOG_FIXED,
    text: 'Fijo',
  },
  [SubscriptionType.DIGITAL_FIXED]: {
    id: SubscriptionType.DIGITAL_FIXED,
    text: 'Fijo digital',
  },
  [SubscriptionType.ADSL]: {
    id: SubscriptionType.ADSL,
    text: 'ADSL',
  },
  [SubscriptionType.FFTH]: {
    id: SubscriptionType.FFTH,
    text: 'Fibra',
  },
  [SubscriptionType.AGILE_TV]: {
    id: SubscriptionType.AGILE_TV,
    text: 'Agile TV',
  },
  [SubscriptionType.UNKNOWN]: {
    id: SubscriptionType.UNKNOWN,
    text: 'Tipo desconocido',
  },
}

export function mergeFixedAndInternetSubscriptions(subs: Subscription[] = []): Subscription[] {
  const filteredSubscriptions = subs.reduce((acc, sub) => {
    const duplicatedItem = acc.find((el) => el.identificationNr === sub.identificationNr)

    if (duplicatedItem) {
      if (duplicatedItem.type !== 'DIGITAL_FIXED') {
        return acc
      }
      return [...acc.filter((el) => el.identificationNr !== sub.identificationNr), sub]
    }

    return [...acc, sub]
  }, [] as Subscription[])

  return filteredSubscriptions
}

export function getTariffId(subscription?: Subscription) {
  return subscription?.commercialProduct.id
}

export function getProvinceId(customer?: Customer) {
  return customer?.addressInfo.stateId
}

export function getAccountFromAccountId(customer?: Customer, accountNumber?: string) {
  return customer?.accounts?.filter((account) => account.accountNumber === accountNumber)[0]
}

export function hasConsumptionData(subs: Subscription) {
  return ['PREPAID', 'POSTPAID'].includes(String(subs.type))
}

export function getEmailFromCustomer(customer?: Customer) {
  return customer?.business ? customer?.business.email : customer?.personal?.email
}

export function isValidEmail(email: string) {
  const EMAIL_REGEXP = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/

  return EMAIL_REGEXP.test(String(email).toLocaleLowerCase())
}
